<script>
  import { onDestroy } from "svelte"
  import { fly, fade } from "svelte/transition"

  let { toasts: initial = [] } = $props()

  const DEFAULT_DURATION = 5000
  // The newest toast in full and a sliver of each one under it, capped: a
  // fourth sliver reads as texture rather than as a number, and anything
  // older than that waits unrendered until the pile in front of it shrinks.
  const MAX_LAYERS = 4
  const reduceMotion =
    typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches
  // Bulma's mobile breakpoint, matching the stack's own media query: below it
  // the toasts sit at the bottom of the screen and so fly in from below it.
  const mobile = typeof matchMedia === "function" && matchMedia("(max-width: 768px)").matches

  let nextId = 0
  // svelte-ignore state_referenced_locally -- islands remount per visit; props seed state once
  let toasts = $state(initial.map((toast) => ({ ...toast, id: nextId++ })))
  // Each buried toast is drawn at exactly the height of the toast in front of
  // it and clipped to it, so every one shows the same sliver whatever it says
  // -- without that, a short toast behind a tall one would not show at all and
  // a tall one behind a short one would hang out of the bottom of the pile.
  let heights = $state({})
  let paused = $state(false)

  // Newest first at both breakpoints: on a desktop the pile hangs down from
  // the top of the window, on a phone it grows up from the bottom of it, and
  // the toast just raised is the one nearest the edge either way.
  let pile = $derived(toasts.slice(-MAX_LAYERS).reverse())
  let front = $derived(toasts.at(-1))
  let frontHeight = $derived(heights[front?.id] ?? 0)

  function add(toast) {
    toasts.push({ ...toast, id: nextId++ })
  }

  function dismiss(id) {
    const index = toasts.findIndex((toast) => toast.id === id)
    if (index !== -1) toasts.splice(index, 1)
    delete heights[id]
  }

  // Bulma notification modifiers, so a toast and the inline shared/_flash a
  // JavaScript-off page renders read as the same thing.
  function kindClass(type) {
    if (type === "notice") return "is-primary"
    if (type === "alert") return "is-danger"
    return "is-info"
  }

  // A toast carrying actions offers them as buttons; a button's own click is
  // what dismisses it, since whatever it does is the last thing it was for.
  // An onClick that returns false declined to act -- a cancelled confirm --
  // and the toast stays for another try.
  function invoke(action, id) {
    if (action.onClick() === false) return

    dismiss(id)
  }

  function layerStyle(layer) {
    const style = `--layer: ${layer}; z-index: ${MAX_LAYERS - layer};`
    if (layer === 0 || frontHeight === 0) return style

    return `${style} height: ${frontHeight}px;`
  }

  // A swipe either way throws the toast in front off the side. Pointer events
  // are one code path for a finger and for a mouse drag, and the move and the
  // release are listened for on the window rather than taken with
  // setPointerCapture: a capture retargets the click that follows onto the
  // captured element, and the buttons inside a toast would stop working.
  const DRAG_THRESHOLD = 6
  const DISMISS_FRACTION = 0.4
  const FLICK_VELOCITY = 0.5
  const SETTLE = 180

  let drag = $state({ id: null, dx: 0, width: 0, settling: false })
  let gesture = null
  let dragged = false

  function startDrag(event, toast) {
    if (toast.id !== front?.id || event.button > 0) return

    gesture = {
      id: toast.id,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      width: event.currentTarget.offsetWidth,
      startedAt: Date.now(),
      dragging: false,
    }
    dragged = false
    window.addEventListener("pointermove", moveDrag)
    window.addEventListener("pointerup", endDrag)
    window.addEventListener("pointercancel", cancelDrag)
  }

  // Nothing moves until the pointer has gone further sideways than down: a
  // press that turns into a scroll is not a swipe, and neither is the jitter
  // of a click.
  function moveDrag(event) {
    if (!gesture || event.pointerId !== gesture.pointerId) return

    const dx = event.clientX - gesture.x
    if (!gesture.dragging) {
      const sideways = Math.abs(dx)
      if (sideways < DRAG_THRESHOLD || sideways <= Math.abs(event.clientY - gesture.y)) return

      gesture.dragging = true
    }
    drag = { id: gesture.id, dx, width: gesture.width, settling: false }
  }

  function endDrag(event) {
    if (!gesture || event.pointerId !== gesture.pointerId) return

    const { id, width, startedAt, dragging } = gesture
    const dx = event.clientX - gesture.x
    stopDragging()
    if (!dragging) return

    dragged = true
    const velocity = Math.abs(dx) / Math.max(Date.now() - startedAt, 1)
    const far = Math.abs(dx) > width * DISMISS_FRACTION
    const flicked = velocity > FLICK_VELOCITY && Math.abs(dx) > DRAG_THRESHOLD * 3

    if (far || flicked) fling(id, dx, width)
    else settleBack(id)
  }

  function cancelDrag(event) {
    if (!gesture || event.pointerId !== gesture.pointerId) return

    const { id, dragging } = gesture
    stopDragging()
    if (dragging) settleBack(id)
  }

  function stopDragging() {
    gesture = null
    window.removeEventListener("pointermove", moveDrag)
    window.removeEventListener("pointerup", endDrag)
    window.removeEventListener("pointercancel", cancelDrag)
  }

  function fling(id, dx, width) {
    if (reduceMotion) {
      dismiss(id)
      return
    }
    drag = { id, dx: Math.sign(dx) * (width + 60), width, settling: true }
    setTimeout(() => {
      clearDrag(id)
      dismiss(id)
    }, SETTLE)
  }

  function settleBack(id) {
    if (reduceMotion) {
      clearDrag(id)
      return
    }
    drag = { id, dx: 0, width: drag.width, settling: true }
    setTimeout(() => clearDrag(id), SETTLE)
  }

  function clearDrag(id) {
    if (drag.id === id) drag = { id: null, dx: 0, width: 0, settling: false }
  }

  // Reduced motion asks for no animation, not for no gesture: the swipe still
  // dismisses, it just does not drag anything across the screen first.
  function dragStyle(toast) {
    if (reduceMotion || drag.id !== toast.id) return ""

    const travelled = Math.min(Math.abs(drag.dx) / (drag.width || 1), 1)
    const transition = drag.settling
      ? `transform ${SETTLE}ms ease-out, opacity ${SETTLE}ms ease-out`
      : "none"

    return ` transform: translateX(${drag.dx}px); opacity: ${1 - travelled * 0.7};` +
      ` transition: ${transition};`
  }

  // The drag that just ended would otherwise land as a click on whatever the
  // pointer came to rest over -- an Undo the swipe never meant to press.
  function swallowClick(event) {
    if (!dragged) return

    dragged = false
    event.preventDefault()
    event.stopPropagation()
  }

  // Only the toast in front counts down. A buried one would otherwise spend
  // its whole duration behind another and be gone before it ever surfaced, so
  // its timer starts when it reaches the front. What is left of the front
  // toast's own duration survives a hover or a focus, which is what makes it
  // long enough to read and to reach the buttons of.
  let countdown = { id: null, remaining: 0 }

  $effect(() => {
    if (!front) return

    if (countdown.id !== front.id) {
      countdown = { id: front.id, remaining: front.duration ?? DEFAULT_DURATION }
    }
    if (paused || drag.id !== null) return

    const startedAt = Date.now()
    const timer = setTimeout(() => dismiss(front.id), countdown.remaining)

    return () => {
      clearTimeout(timer)
      countdown.remaining -= Date.now() - startedAt
    }
  })

  // Other islands can raise a toast client-side by dispatching on document:
  // document.dispatchEvent(new CustomEvent("toast", { detail: { type, message } }))
  // The detail may also carry a duration in milliseconds and an actions array,
  // each entry either { label, href } or { label, onClick }.
  //
  // Registered as this component is created rather than from an $effect: Svelte
  // runs no effect until every island on the page has been mounted, and an
  // island earlier in the document raises its first toast from one of its own.
  function handleToast(event) {
    add(event.detail)
  }

  document.addEventListener("toast", handleToast)
  onDestroy(() => {
    document.removeEventListener("toast", handleToast)
    // Turbo can replace the page mid-swipe, and the window listeners a gesture
    // puts up outlive the island that put them there.
    stopDragging()
  })

  // Reduced motion keeps the opacity fade but drops the vertical slide (y: 0).
  const flyIn = reduceMotion ? { y: 0, duration: 200 } : { y: mobile ? 16 : -16, duration: 250 }
</script>

<div class="toast-stack">
  {#each pile as toast, layer (toast.id)}
    <div
      class="notification {kindClass(toast.type)}"
      class:is-buried={layer > 0}
      style={layerStyle(layer) + dragStyle(toast)}
      inert={layer > 0}
      bind:clientHeight={heights[toast.id]}
      role="status"
      aria-live="polite"
      in:fly={flyIn}
      out:fade={{ duration: 200 }}
      onmouseenter={() => (paused = true)}
      onmouseleave={() => (paused = false)}
      onfocusin={() => (paused = true)}
      onfocusout={() => (paused = false)}
      onpointerdown={(event) => startDrag(event, toast)}
      onclickcapture={swallowClick}
    >
      <button class="delete" aria-label="Dismiss notification" onclick={() => dismiss(toast.id)}></button>
      {toast.message}
      {#if toast.actions?.length}
        <div class="buttons are-small toast-actions">
          {#each toast.actions as action (action.label)}
            {#if action.href}
              <a class="button is-small" href={action.href}>{action.label}</a>
            {:else}
              <button type="button" class="button is-small" onclick={() => invoke(action, toast.id)}>
                {action.label}
              </button>
            {/if}
          {/each}
        </div>
      {/if}
    </div>
  {/each}
</div>

<style>
  /* Zero height by design: the toasts inside are absolutely positioned, one on
     top of the next, and this only says where the front of the pile sits. */
  .toast-stack {
    position: fixed;
    top: 1rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
    width: min(360px, calc(100vw - 2rem));
  }

  /* .notification is bulma's, which svelte would not scope to this component
     -- hence :global. Bulma spaces stacked notifications with a bottom margin
     that the pile has no use for; it reserves no room for the .delete it
     absolutely positions, so a message long enough runs under it; and a
     floating overlay wants a shadow an in-page notification does not.

     Each layer is inset and offset from the one in front of it, so what shows
     of a buried toast is the edge of a card behind a card. Moving between
     layers is a change of offset rather than of transform, which leaves
     transform to the swipe. */
  .toast-stack > :global(.notification) {
    position: absolute;
    top: calc(var(--layer) * 10px);
    left: calc(var(--layer) * 6px);
    right: calc(var(--layer) * 6px);
    margin-bottom: 0;
    padding-right: 2.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    /* A sideways drag on a toast is the swipe, not the page scrolling under
       it; a vertical one is still the page. */
    touch-action: pan-y;
    transition: top 0.2s ease, bottom 0.2s ease, left 0.2s ease, right 0.2s ease,
      height 0.2s ease;
  }

  /* The height layerStyle imposes is the front toast's, and this is what makes
     it a clip rather than a stretch. */
  .toast-stack > :global(.notification.is-buried) {
    overflow: hidden;
  }

  /* A phone reads and dismisses toasts with a thumb, so the stack drops to the
     bottom of the screen, above whatever the device puts there, and runs the
     width of it. The pile then grows upwards, which keeps the newest toast
     lowest -- nearest the thumb.

     After the rule it overrides rather than before it: both name the same
     element with the same specificity, so the later one is the one that wins
     and a phone would otherwise keep the desktop's top offsets. */
  @media screen and (max-width: 768px) {
    .toast-stack {
      top: auto;
      bottom: calc(0.75rem + env(safe-area-inset-bottom, 0px));
      left: 0.75rem;
      right: 0.75rem;
      width: auto;
      transform: none;
    }

    .toast-stack > :global(.notification) {
      top: auto;
      bottom: calc(var(--layer) * 10px);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .toast-stack > :global(.notification) {
      transition: none;
    }
  }

  /* Bulma's .buttons hangs a negative bottom margin off the last child of a
     block, which here would eat into the notification's own padding. */
  .toast-actions {
    margin-top: 0.75rem;
    margin-bottom: 0;
  }
</style>
